// Iter83 — Platform-admin operational analytics dashboard.
//
// Focused, intentionally small surface:
//   - Top summary (most/least active, on-time % vs overdue %, dormant tenants).
//   - Per-user table (the core) — sortable columns + XLSX export.
//   - Click a row → side drawer with Recharts visuals + effectiveness numbers.
//   - Date-range + tenant filters.
//
// Strictly platform_admin only — Sidebar/Route already enforce this; the
// backend re-enforces via require_platform_admin.
import React, { useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart as RLineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Download, X, ArrowUpDown } from "lucide-react";
import { useTenants } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDuration = (sec) => {
  if (!sec || sec < 0) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const todayMinus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

// ──────────────────────────────────────────────────────────────────────────
// Hooks
// ──────────────────────────────────────────────────────────────────────────

function useAnalyticsUsers(since, until, tenantId) {
  return useQuery({
    queryKey: ["analytics-users", since, until, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (since) params.set("since", `${since}T00:00:00Z`);
      if (until) params.set("until", `${until}T23:59:59Z`);
      if (tenantId) params.set("tenant_id", tenantId);
      const { data } = await axios.get(`${API}/admin/analytics/users?${params.toString()}`);
      return data;
    },
  });
}

function useAnalyticsSummary(since, until) {
  return useQuery({
    queryKey: ["analytics-summary", since, until],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (since) params.set("since", `${since}T00:00:00Z`);
      if (until) params.set("until", `${until}T23:59:59Z`);
      const { data } = await axios.get(`${API}/admin/analytics/summary?${params.toString()}`);
      return data;
    },
  });
}

function useAnalyticsUserDetail(userId, since, until) {
  return useQuery({
    queryKey: ["analytics-user", userId, since, until],
    enabled: !!userId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (since) params.set("since", `${since}T00:00:00Z`);
      if (until) params.set("until", `${until}T23:59:59Z`);
      const { data } = await axios.get(`${API}/admin/analytics/user/${userId}?${params.toString()}`);
      return data;
    },
  });
}

// ──────────────────────────────────────────────────────────────────────────

const SORT_KEYS = {
  name: (r) => (r.name || r.email || "").toLowerCase(),
  login_count: (r) => r.login_count || 0,
  total_seconds: (r) => r.total_seconds || 0,
  incidents_owned: (r) => r.incidents_owned || 0,
  ontime_pct: (r) => (r.ontime_pct == null ? -1 : r.ontime_pct),
  overdue_count: (r) => r.overdue_count || 0,
  last_login: (r) => r.last_login || "",
};

export default function AdminAnalytics() {
  const [since, setSince] = useState(todayMinus(7));
  const [until, setUntil] = useState(today());
  const [tenantId, setTenantId] = useState("");
  const [sortKey, setSortKey] = useState("login_count");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedUserId, setSelectedUserId] = useState(null);

  const tenants = useTenants();
  const usersQ = useAnalyticsUsers(since, until, tenantId);
  const summaryQ = useAnalyticsSummary(since, until);

  const sortedRows = useMemo(() => {
    const rows = usersQ.data?.rows || [];
    const get = SORT_KEYS[sortKey] || SORT_KEYS.login_count;
    const out = [...rows].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [usersQ.data, sortKey, sortDir]);

  const handleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  const downloadXlsx = async () => {
    const params = new URLSearchParams();
    if (since) params.set("since", `${since}T00:00:00Z`);
    if (until) params.set("until", `${until}T23:59:59Z`);
    if (tenantId) params.set("tenant_id", tenantId);
    const res = await axios.get(`${API}/admin/analytics/users/xlsx?${params.toString()}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sitroom-user-analytics-${since}_${until}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="px-6 py-6" data-testid="admin-analytics-page">
      <Header
        since={since} until={until} tenantId={tenantId}
        tenants={tenants.data || []}
        onSince={setSince} onUntil={setUntil} onTenant={setTenantId}
        onExport={downloadXlsx}
      />

      <SummaryCards data={summaryQ.data} loading={summaryQ.isLoading} />

      <UsersTable
        rows={sortedRows}
        loading={usersQ.isLoading}
        sortKey={sortKey} sortDir={sortDir}
        onSort={handleSort}
        onRowClick={(r) => setSelectedUserId(r.user_id)}
        tenantById={Object.fromEntries((tenants.data || []).map((t) => [t.id, t.name]))}
      />

      {selectedUserId && (
        <UserDetailDrawer
          userId={selectedUserId}
          since={since}
          until={until}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────── header / filters ───────────────────────────

const Header = ({ since, until, tenantId, tenants, onSince, onUntil, onTenant, onExport }) => (
  <div className="mb-5">
    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
      <div>
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22, color: "#f0f2f8", letterSpacing: "-0.4px" }}>
          User Analytics
        </h1>
        <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.5px", textTransform: "uppercase", marginTop: 2 }}>
          Platform operator view · Tenants cannot access this page
        </p>
      </div>
      <button
        type="button" onClick={onExport}
        className="inline-flex items-center gap-2 px-3 py-2 rounded"
        style={{ background: "rgba(24,95,165,0.16)", border: "1px solid rgba(24,95,165,0.55)", color: "#9ec3ec", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.5px", cursor: "pointer" }}
        data-testid="analytics-export-xlsx"
      >
        <Download size={13} /> EXPORT XLSX
      </button>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <FilterChip>
        <span>Since</span>
        <input type="date" value={since} onChange={(e) => onSince(e.target.value)} className="bg-transparent outline-none" style={{ color: "#f0f2f8" }} data-testid="analytics-since" />
      </FilterChip>
      <FilterChip>
        <span>Until</span>
        <input type="date" value={until} onChange={(e) => onUntil(e.target.value)} className="bg-transparent outline-none" style={{ color: "#f0f2f8" }} data-testid="analytics-until" />
      </FilterChip>
      <FilterChip>
        <span>Tenant</span>
        <select value={tenantId} onChange={(e) => onTenant(e.target.value)} className="bg-transparent outline-none" style={{ color: "#f0f2f8" }} data-testid="analytics-tenant">
          <option value="">All tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id} style={{ background: "#0d0f18" }}>{t.name || t.id}</option>
          ))}
        </select>
      </FilterChip>
      <PresetButton label="7d" onClick={() => { onSince(todayMinus(7)); onUntil(today()); }} />
      <PresetButton label="30d" onClick={() => { onSince(todayMinus(30)); onUntil(today()); }} />
      <PresetButton label="90d" onClick={() => { onSince(todayMinus(90)); onUntil(today()); }} />
    </div>
  </div>
);

const FilterChip = ({ children }) => (
  <label
    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded"
    style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0", letterSpacing: "0.3px" }}
  >
    {children}
  </label>
);

const PresetButton = ({ label, onClick }) => (
  <button
    type="button" onClick={onClick}
    className="px-2 py-1 rounded"
    style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#9ec3ec", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", cursor: "pointer" }}
    data-testid={`analytics-preset-${label}`}
  >
    {label}
  </button>
);

// ─────────────────────────── summary cards ───────────────────────────

const SummaryCards = ({ data, loading }) => {
  const totals = data?.totals || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      <Card title="Activity" testid="analytics-summary-activity">
        {loading ? <Empty>Loading…</Empty> : (
          <>
            <Stat label="Active users (range)" value={totals.active_users_in_range ?? "—"} />
            <Stat label="Events captured" value={totals.total_events_in_range ?? "—"} />
            <Stat label="Total users" value={totals.total_users ?? "—"} />
          </>
        )}
      </Card>
      <Card title="On-time vs Overdue (combined)" testid="analytics-summary-ontime">
        {loading ? <Empty>Loading…</Empty> : (
          <>
            <Stat label="On-time %" value={totals.ontime_pct != null ? `${totals.ontime_pct}%` : "—"} color="#4ed084" />
            <Stat label="On-time" value={totals.ontime_count ?? "—"} />
            <Stat label="Overdue" value={totals.overdue_count ?? "—"} color="#E24B4A" />
          </>
        )}
      </Card>
      <Card title={`Dormant tenants (≥${data?.dormant_days_threshold || 7}d)`} testid="analytics-summary-dormant">
        {loading ? <Empty>Loading…</Empty> : (
          <div className="space-y-1" data-testid="dormant-tenant-list">
            {(data?.dormant_tenants || []).slice(0, 8).map((t) => (
              <div key={t.tenant_id} style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#f3b14a" }}>
                · {t.name || t.tenant_id}
              </div>
            ))}
            {(!data?.dormant_tenants || data.dormant_tenants.length === 0) && <Empty>None — all tenants active</Empty>}
          </div>
        )}
      </Card>
    </div>
  );
};

const Card = ({ title, children, testid }) => (
  <div className="rounded-md p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={testid}>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#c5cad8", marginBottom: 8, letterSpacing: "0.3px" }}>{title}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Stat = ({ label, value, color = "#f0f2f8" }) => (
  <div className="flex items-baseline justify-between">
    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.3px" }}>{label}</span>
    <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color }}>{value}</span>
  </div>
);

const Empty = ({ children }) => (
  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>{children}</div>
);

// ─────────────────────────── users table ───────────────────────────

const TH = ({ label, k, sortKey, sortDir, onSort, align = "left" }) => (
  <button
    type="button" onClick={() => onSort(k)}
    className="inline-flex items-center gap-1"
    style={{ background: "transparent", border: "none", color: sortKey === k ? "#9ec3ec" : "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "1.5px", textTransform: "uppercase", cursor: "pointer", textAlign: align, width: "100%" }}
    data-testid={`analytics-sort-${k}`}
  >
    {label} <ArrowUpDown size={9} style={{ opacity: sortKey === k ? 1 : 0.4 }} />
    {sortKey === k && <span style={{ marginLeft: 2 }}>{sortDir === "asc" ? "↑" : "↓"}</span>}
  </button>
);

const UsersTable = ({ rows, loading, sortKey, sortDir, onSort, onRowClick, tenantById }) => (
  <div className="rounded-md overflow-hidden" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }}>
    <div
      className="grid items-center px-3 py-2"
      style={{
        gridTemplateColumns: "1.6fr 1.3fr 0.9fr 0.7fr 1fr 1fr 0.9fr 0.9fr 0.9fr",
        background: "rgba(255,255,255,0.03)", borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <TH label="User" k="name" {...{ sortKey, sortDir, onSort }} />
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase" }}>Tenant · Role</div>
      <TH label="Logins" k="login_count" {...{ sortKey, sortDir, onSort }} align="right" />
      <TH label="Time" k="total_seconds" {...{ sortKey, sortDir, onSort }} align="right" />
      <TH label="Last login" k="last_login" {...{ sortKey, sortDir, onSort }} />
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase" }}>Top modules</div>
      <TH label="Owned" k="incidents_owned" {...{ sortKey, sortDir, onSort }} align="right" />
      <TH label="Overdue" k="overdue_count" {...{ sortKey, sortDir, onSort }} align="right" />
      <TH label="On-time %" k="ontime_pct" {...{ sortKey, sortDir, onSort }} align="right" />
    </div>
    {loading && (
      <div className="px-3 py-6 text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}>
        Loading user analytics…
      </div>
    )}
    {!loading && rows.length === 0 && (
      <div className="px-3 py-6 text-center" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}>
        No data for the selected date range.
      </div>
    )}
    {rows.map((r) => (
      <div
        key={r.user_id}
        onClick={() => onRowClick(r)}
        className="grid items-center px-3 py-2 cursor-pointer transition-colors"
        style={{
          gridTemplateColumns: "1.6fr 1.3fr 0.9fr 0.7fr 1fr 1fr 0.9fr 0.9fr 0.9fr",
          borderBottom: "1px solid rgba(255,255,255,0.04)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(24,95,165,0.06)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        data-testid={`analytics-user-row-${r.user_id}`}
      >
        <div>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#f0f2f8" }}>{r.name || "—"}</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0" }}>{r.email}</div>
        </div>
        <div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}>{tenantById[r.tenant_id] || r.tenant_id || "—"}</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0" }}>{r.role}</div>
        </div>
        <div className="text-right" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: r.login_count ? "#9ec3ec" : "#9198b0", fontWeight: 700 }}>
          {r.login_count || 0}
        </div>
        <div className="text-right" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8" }}>
          {fmtDuration(r.total_seconds)}
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}>
          {r.last_login ? fmtDate(r.last_login) : "—"}
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c89adb" }}>
          {(r.top_modules || []).slice(0, 3).map((m) => `${m.module}(${m.count})`).join(", ") || "—"}
        </div>
        <div className="text-right" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8" }}>{r.incidents_owned || 0}</div>
        <div className="text-right" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: r.overdue_count ? "#E24B4A" : "#c5cad8", fontWeight: r.overdue_count ? 700 : 400 }}>
          {r.overdue_count || 0}
        </div>
        <div className="text-right" style={{ fontFamily: "Syne, sans-serif", fontSize: 12, fontWeight: 700, color: r.ontime_pct == null ? "#9198b0" : r.ontime_pct >= 90 ? "#4ed084" : r.ontime_pct >= 70 ? "#f3b14a" : "#E24B4A" }}>
          {r.ontime_pct == null ? "—" : `${r.ontime_pct}%`}
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────── detail drawer ───────────────────────────

const UserDetailDrawer = ({ userId, since, until, onClose }) => {
  const detail = useAnalyticsUserDetail(userId, since, until);

  return (
    <>
      <div className="fixed inset-0 z-[9990]" style={{ background: "rgba(8,9,13,0.55)" }} onClick={onClose} />
      <div
        className="fixed right-0 top-0 bottom-0 z-[9991] overflow-y-auto"
        style={{ width: "min(640px, 100vw)", background: "#0a0c14", borderLeft: "1px solid rgba(255,255,255,0.08)" }}
        data-testid="analytics-user-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "#f0f2f8" }}>
              {detail.data?.user?.name || detail.data?.user?.email || "User detail"}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
              {detail.data?.user?.email} · {detail.data?.user?.role}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded" style={{ color: "#c5cad8", background: "transparent", border: "1px solid rgba(255,255,255,0.10)", cursor: "pointer" }} data-testid="analytics-drawer-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {detail.isLoading ? (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9198b0" }}>Loading…</div>
          ) : detail.data ? (
            <>
              <EffectivenessCard data={detail.data.effectiveness} />
              <ActivityChart days={detail.data.activity_per_day || []} />
              <ModuleChart modules={detail.data.modules || []} />
              <RecentSessions sessions={detail.data.sessions || []} />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

const EffectivenessCard = ({ data }) => {
  if (!data) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="analytics-drawer-effectiveness">
      <MiniStat label="On-time %" value={data.ontime_pct == null ? "—" : `${data.ontime_pct}%`} color={data.ontime_pct == null ? "#9198b0" : data.ontime_pct >= 90 ? "#4ed084" : data.ontime_pct >= 70 ? "#f3b14a" : "#E24B4A"} />
      <MiniStat label="MTTR" value={data.mttr_seconds ? fmtDuration(data.mttr_seconds) : "—"} />
      <MiniStat label="Owned" value={data.incidents_owned ?? 0} />
      <MiniStat label="Resolved" value={data.incidents_resolved ?? 0} color="#4ed084" />
      <MiniStat label="Closed" value={data.incidents_closed ?? 0} />
      <MiniStat label="Overdue" value={data.overdue_count ?? 0} color={data.overdue_count ? "#E24B4A" : "#c5cad8"} />
    </div>
  );
};

const MiniStat = ({ label, value, color = "#f0f2f8" }) => (
  <div className="rounded p-2.5" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }}>
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1px", textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 18, color, marginTop: 2 }}>{value}</div>
  </div>
);

const ActivityChart = ({ days }) => (
  <div className="rounded p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="analytics-drawer-activity-chart">
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#c5cad8", marginBottom: 8 }}>Activity over time</div>
    <ResponsiveContainer width="100%" height={180}>
      <RLineChart data={days} margin={{ top: 6, right: 10, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="date" stroke="#5f677c" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
        <YAxis stroke="#5f677c" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9 }} allowDecimals={false} />
        <Tooltip contentStyle={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.12)", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
        <Line type="monotone" dataKey="actions" stroke="#9ec3ec" strokeWidth={2} dot={false} name="actions" />
        <Line type="monotone" dataKey="logins" stroke="#4ed084" strokeWidth={2} dot={false} name="logins" />
      </RLineChart>
    </ResponsiveContainer>
  </div>
);

const ModuleChart = ({ modules }) => (
  <div className="rounded p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="analytics-drawer-module-chart">
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#c5cad8", marginBottom: 8 }}>Module usage</div>
    {modules.length === 0 ? (
      <Empty>No module events in range.</Empty>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(160, modules.length * 22)}>
        <BarChart data={modules} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis type="number" stroke="#5f677c" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9 }} allowDecimals={false} />
          <YAxis dataKey="module" type="category" stroke="#5f677c" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9 }} width={120} />
          <Tooltip contentStyle={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.12)", fontFamily: "'DM Mono', monospace", fontSize: 11 }} />
          <Bar dataKey="count" fill="#c89adb" radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const RecentSessions = ({ sessions }) => (
  <div className="rounded p-3" style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="analytics-drawer-sessions">
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: "#c5cad8", marginBottom: 8 }}>Recent sessions</div>
    {sessions.length === 0 ? (
      <Empty>No sessions in range.</Empty>
    ) : (
      <div className="space-y-1">
        {sessions.slice(0, 10).map((s) => (
          <div key={s.session_id} className="flex items-center justify-between" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8" }}>
            <span>{fmtDate(s.started_at)}</span>
            <span style={{ color: s.ended_at ? "#9aebc2" : "#f3b14a" }}>
              {s.ended_at ? `· ${fmtDuration(s.duration_seconds)}` : "· active"}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
